import { Integrations as ApmIntegrations } from '@sentry/apm';
import * as Sentry from "@sentry/browser";
import { CaptureConsole } from '@sentry/integrations';
import { Options as SentryOptions } from "@sentry/types";
import { SentryHubProvider } from '../sentry/sentry-hub.provider';
import { AngularSentryIntegration } from '../sentry/sentry-integration';
import { EmployeeService } from "../services";

const ignoreErrors = [
    // Ignore the common conventions for closing modals
    /^Possibly unhandled rejection: (cancel|close|escape|backdrop click)/,
    /^(cancel|close|escape|backdrop click|escape key press)$/,
    // Ignore messages logged by Webpack Dev Server while debugging
    /^\[WDS\]/,
    // Similarly, ignore Hot Module Reload errors.
    /^\[HMR\]/,
    // Ignore build errors while debugging.
    /Module build failed/,
    /^(webpack)/,
    // Ignore browsers that are incompatible with this metric.
    "Ignoring unsupported entryTypes: largest-contentful-paint.",
    // Ignore errors that result from xhrStatus=abort
    "AbortedXhrError",
    "The connection was aborted",
    // This error is benign: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
    "ResizeObserver loop limit exceeded",
    // This is logged in between releases, when a user is on an old build and tries to lazy load a module.
    // It's already handled by a ui-router transition, so ignore it.
    "SyntaxError: Unexpected token '<'"
]

export function configureSentry(sentryOptions: SentryOptions,
    sentryLogHubProvider: SentryHubProvider,
    sentryExceptionHandlerHubProvider: SentryHubProvider) {
    'ngInject';
    if (sentryOptions.enabled) {
        try {
            Sentry.init({
                // Extend the partial Sentry authentication and environment options.
                ...sentryOptions,
                integrations: [
                    new AngularSentryIntegration(sentryLogHubProvider, sentryExceptionHandlerHubProvider),
                    new CaptureConsole({ levels: ["warn", "error"] }),
                    new ApmIntegrations.Tracing()
                ],
                ignoreErrors,
                // Attaching the stack trace can be extremely useful for areas that just call console.error on errors,
                // which wouldn't hit our AngularJS error wrapper to attach a stack trace closer to the error.
                attachStacktrace: true
            });
        } catch (ex) {
            console.warn(ex, "Could not initialize Sentry.");
        }
    }
}

export function configureSentryUser(employeeService: EmployeeService, apexIsAuthenticated: boolean) {
    'ngInject';
    if (!apexIsAuthenticated) {
        return;
    }
    employeeService.retrieveIdentity()
        .then(identity => {
            try {
                Sentry.setUser({
                    id: `${identity.EmployeeID}`
                })
            } catch (ex) {
                console.warn(ex, "Could not set Sentry user.");
            }
        })
        .catch(() => {})
}
